/*
 * Figma `126:701` — SA3-12 · Vendor Logs.
 *
 * `TableTemplate`: what was asked of each provider, what came back, and how long it took — with the
 * response body behind a deliberate click rather than in the list.
 *
 * The capture mode is the part of this screen with real consequences, and the frame puts it at the
 * top for that reason. `full` records the raw provider response, which can carry a real customer's
 * name and bank account. The server turns it off by itself and deletes what it captured; this
 * screen makes that deadline visible *while* it is running, because a capture mode nobody remembers
 * enabling is how a debugging convenience becomes a retention incident.
 *
 * Turning it on goes through a consequence dialog rather than a `window.confirm`, and the dialog
 * names what gets recorded rather than asking whether you are sure.
 */
import {
  Alert, Button, Card, ConfirmDialog, Drawer, FilterChips, KpiCard, Select, StatusBadge,
  TableTemplate, Toggle,
} from '../..';
import type { BadgeTone, Column } from '../..';
import { useState } from 'react';
import type { Destination } from '../../destinations';
import type { VendorExchangeDto } from '../../../api';
import type { VendorLogsState } from './useVendorLogs';
import { useVendorLogs } from './useVendorLogs';

const when = (iso: string) => new Date(iso).toLocaleString('th-TH');

function outcomeTone(row: VendorExchangeDto): BadgeTone {
  if (row.outcome === 'ok' || (row.httpStatus !== null && row.httpStatus < 400)) return 'success';
  if (row.httpStatus !== null && row.httpStatus >= 500) return 'danger';
  return 'warning';
}

export function VendorLogsScreen({ destination, state }: {
  destination: Destination;
  state: VendorLogsState;
}) {
  const {
    items, capture, loading, loadError, error, vendorFilter, setVendorFilter,
    errorsOnly, setErrorsOnly, openId, open, body, bodyLoading, setCaptureMode, reload,
  } = state;
  const [confirmFull, setConfirmFull] = useState(false);

  const vendors = Array.from(new Set(items.map((i) => i.vendor)));
  const rows = items.filter((i) => {
    if (vendorFilter !== 'all' && i.vendor !== vendorFilter) return false;
    if (errorsOnly && outcomeTone(i) === 'success') return false;
    return true;
  });

  const failures = items.filter((i) => outcomeTone(i) !== 'success').length;
  const slowest = items.reduce((max, i) => Math.max(max, i.durationMs), 0);
  const openRow = openId ? items.find((i) => i.id === openId) ?? null : null;

  const columns: Column<VendorExchangeDto>[] = [
    { key: 'when', header: 'เมื่อ', width: '190px', cell: (r) => when(r.createdAt) },
    {
      key: 'vendor',
      header: 'ผู้ให้บริการ',
      width: '200px',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{r.vendor}</span>
          <span className="jt-cell-sub jt-mono">{r.operation}</span>
        </span>
      ),
    },
    { key: 'path', header: 'เรียกอะไร', cell: (r) => <span className="jt-mono">{r.requestPath}</span> },
    {
      key: 'order',
      header: 'ออร์เดอร์',
      width: '150px',
      cell: (r) => (r.orderCode ? <span className="jt-mono">{r.orderCode}</span> : '—'),
    },
    { key: 'duration', header: 'ใช้เวลา', align: 'end', width: '110px', cell: (r) => <span className="jt-mono">{r.durationMs} ms</span> },
    {
      key: 'outcome',
      header: 'ผล',
      width: '160px',
      cell: (r) => (
        <span className="jt-inline-actions">
          <StatusBadge tone={outcomeTone(r)}>{r.httpStatus ?? r.outcome}</StatusBadge>
          {r.captureMode === 'full' && <StatusBadge tone="warning">เก็บเต็ม</StatusBadge>}
        </span>
      ),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload }}
      kpis={[
        <KpiCard key="all" label="การเรียกที่บันทึกไว้" value={items.length.toLocaleString('th-TH')} />,
        <KpiCard key="fail" label="ที่ไม่สำเร็จ" value={failures.toLocaleString('th-TH')} tone={failures > 0 ? 'danger' : undefined} />,
        <KpiCard key="slow" label="ช้าที่สุด" value={`${slowest} ms`} />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          <Card title="โหมดการเก็บข้อมูล" sub="กำหนดว่าคำตอบจากผู้ให้บริการถูกบันทึกไว้แค่ไหน">
            <div className="jt-inline-actions">
              <Select
                label="โหมด"
                value={capture?.mode ?? 'off'}
                options={[
                  { value: 'off', label: 'ไม่เก็บเลย' },
                  { value: 'redacted', label: 'เก็บแบบปิดข้อมูลส่วนบุคคล' },
                  { value: 'full', label: 'เก็บเต็ม (ชั่วคราว)' },
                ]}
                onChange={(e) => {
                  const mode = e.target.value as 'off' | 'redacted' | 'full';
                  if (mode === 'full') setConfirmFull(true);
                  else void setCaptureMode(mode);
                }}
              />
            </div>
            {capture?.mode === 'full' && (
              <Alert tone="warning" title="กำลังเก็บคำตอบดิบอยู่">
                คำตอบที่บันทึกในโหมดนี้อาจมีชื่อและเลขบัญชีจริงของลูกค้า
                {capture.fullModeUntil && ` — ระบบจะปิดให้เองเมื่อ ${when(capture.fullModeUntil)} แล้วลบข้อมูลที่เก็บไว้`}
              </Alert>
            )}
            {capture?.mode === 'off' && (
              <p className="jt-field-hint">ไม่มีการบันทึกคำตอบ — ตารางด้านล่างจะไม่มีรายการใหม่เพิ่มเข้ามา</p>
            )}
          </Card>
        </>
      }
      toolbar={
        <>
          <FilterChips
            chips={[{ key: 'all', label: 'ทุกผู้ให้บริการ' }, ...vendors.map((v) => ({ key: v, label: v }))]}
            active={vendorFilter}
            onSelect={setVendorFilter}
            label="ผู้ให้บริการ"
          />
          <Toggle
            label="เฉพาะที่ไม่สำเร็จ"
            checked={errorsOnly}
            onChange={(e) => setErrorsOnly(e.target.checked)}
          />
        </>
      }
      table={{
        label: 'การตอบกลับจากผู้ให้บริการ',
        columns,
        rows,
        rowKey: (r) => r.id,
        filtered: vendorFilter !== 'all' || errorsOnly,
        onClearFilters: () => { setVendorFilter('all'); setErrorsOnly(false); },
        emptyTitle: 'ยังไม่มีการเรียกที่บันทึกไว้',
        emptyText: 'เปิดโหมดการเก็บข้อมูลด้านบนเพื่อเริ่มบันทึก',
        onRowClick: (r) => open(r.id),
        activeRowKey: openId,
        card: (r) => (
          <span className="jt-cell-stack">
            <span>{r.vendor} · {r.operation}</span>
            <span className="jt-cell-sub jt-mono">{r.requestPath} · {r.durationMs} ms</span>
            <StatusBadge tone={outcomeTone(r)}>{r.httpStatus ?? r.outcome}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
          <Drawer open={!!openRow} onClose={() => open(null)} title={openRow ? `${openRow.vendor} · ${openRow.operation}` : ''}>
            {openRow && (
              <>
                <Card title="สรุปการเรียก">
                  <ul className="jt-order-lines">
                    <li><span>เมื่อ</span><span>{when(openRow.createdAt)}</span></li>
                    <li><span>ปลายทาง</span><span className="jt-mono">{openRow.requestPath}</span></li>
                    <li><span>สถานะ</span><span className="jt-mono">{openRow.httpStatus ?? openRow.outcome}</span></li>
                    <li><span>ใช้เวลา</span><span className="jt-mono">{openRow.durationMs} ms</span></li>
                    <li><span>ออร์เดอร์</span><span className="jt-mono">{openRow.orderCode ?? '—'}</span></li>
                    <li className="jt-order-total"><span>ลบอัตโนมัติเมื่อ</span><span>{when(openRow.expiresAt)}</span></li>
                  </ul>
                </Card>

                <Card
                  title="คำตอบที่บันทึกไว้"
                  sub={openRow.captureMode === 'full' ? 'บันทึกแบบเต็ม — อาจมีข้อมูลส่วนบุคคล' : 'ปิดข้อมูลส่วนบุคคลไว้แล้ว'}
                >
                  {!openRow.hasBody
                    ? <p className="jt-field-hint">การเรียกนี้ไม่ได้เก็บคำตอบไว้</p>
                    : bodyLoading
                      ? <p className="jt-field-hint">กำลังเปิด…</p>
                      : body
                        ? (
                          <>
                            {body.bodyTruncated && (
                              <Alert tone="info" title="คำตอบถูกตัดให้สั้นลง">
                                เก็บเฉพาะส่วนต้นของคำตอบเพื่อจำกัดขนาดที่บันทึก
                              </Alert>
                            )}
                            <pre className="jt-vendor-body">{body.body}</pre>
                          </>
                        )
                        : <p className="jt-field-hint">เปิดคำตอบไม่สำเร็จ</p>}
                </Card>
              </>
            )}
          </Drawer>

          <ConfirmDialog
            open={confirmFull}
            onClose={() => setConfirmFull(false)}
            onConfirm={() => { setConfirmFull(false); void setCaptureMode('full'); }}
            title="เปิดโหมดเก็บคำตอบแบบเต็ม"
            confirmLabel="เปิดโหมดเก็บเต็ม"
            tone="warning"
            consequences={[
              'คำตอบดิบจากผู้ให้บริการจะถูกบันทึก ซึ่งอาจมีชื่อจริงและเลขบัญชีของลูกค้า',
              'ระบบจะปิดโหมดนี้ให้เองภายใน 48 ชั่วโมง และลบข้อมูลที่เก็บไว้เมื่อครบกำหนด',
              'ใช้เฉพาะตอนไล่ปัญหาที่ต้องเห็นคำตอบจริงเท่านั้น — ปิดกลับทันทีเมื่อเสร็จ',
            ]}
          />
        </>
      }
    />
  );
}

export function VendorLogsDestination({ token, destination }: { token: string; destination: Destination }) {
  const state = useVendorLogs(token);
  return <VendorLogsScreen destination={destination} state={state} />;
}
